/**
 * La Pose au m² : un seul calcul pour la fiche, le panier et la Personnalisation.
 *
 * Le produit « La Pose » a deux variantes :
 *   · « Forfait jusqu'à 5 m² » (149 €), en quantité 1 ;
 *   · « Au m² » (27,90 €), en quantité = surface.
 * La surface est la somme des m² facturés des décors du panier : les lignes
 * qui portent la propriété « Surface facturée » (fiche) ou « Surface »
 * (Personnalisation). Au prix exact, la surface se lit dans « Surface
 * facturée » ; sinon la quantité EST le nombre de m² (voir lineM2).
 * Au-delà du plafond (20 m²), la pose passe sur devis et ne se commande pas.
 *
 * Après chaque mise à jour du panier, sync() remet la ligne pose d'accord avec
 * les décors (surface, variante, quantité), ou la retire si elle n'a plus lieu
 * d'être. C'est ce qui garantit que le panier facture le prix affiché.
 */
(() => {
  if (window.MsPose) return;

  /** @returns {{handle:string, forfait:{id:number, price:number}|null, m2:{id:number, price:number}|null, max:number, countries:string[], country:string, prop:string}|null} */
  const readConfig = () => {
    const el = document.getElementById('ms-pose-config');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch {
      return null;
    }
  };

  const config = readConfig();

  /** « 279 € », « 167,40 € » : comme money_without_trailing_zeros. */
  const money = (cents) => {
    const euros = Math.floor(cents / 100);
    const rest = Math.round(cents % 100);
    const whole = String(euros).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
    return rest ? `${whole},${String(rest).padStart(2, '0')} €` : `${whole} €`;
  };

  /**
   * Prix de la pose pour une surface donnée.
   * @param {number} m2 - m² facturés (entier)
   * @returns {{state:'none'|'ok'|'over'|'unavailable', m2:number, price?:number, variantId?:number, quantity?:number}}
   */
  const quote = (m2) => {
    const surface = Math.max(0, Math.ceil(Number(m2) || 0));
    if (!config?.forfait || !config?.m2) return { state: 'unavailable', m2: surface };
    if (surface <= 0) return { state: 'none', m2: 0 };
    if (surface > config.max) return { state: 'over', m2: surface };
    const auM2 = config.m2.price * surface;
    // Le forfait couvre les petites surfaces : on prend le prix le plus élevé
    // des deux, soit « 27,90 € le m², minimum 149 € ».
    if (auM2 < config.forfait.price) {
      return { state: 'ok', m2: surface, price: config.forfait.price, variantId: config.forfait.id, quantity: 1 };
    }
    return { state: 'ok', m2: surface, price: auM2, variantId: config.m2.id, quantity: surface };
  };

  const countryAllowed = () => Boolean(config && config.countries.includes(config.country));

  /** @param {any} item */
  const isPose = (item) => item.handle === config?.handle;
  /** @param {any} item */
  // L'Étanche (fibre de verre) n'entre pas dans La Pose au m² : sa pose se fait avec la conciergerie.
  const isDecor = (item) => !isPose(item) && item.handle !== 'l-etanche' && Boolean(item.properties && (item.properties.Surface || item.properties['Surface facturée']));

  /**
   * Les m² d'une ligne de décor. Facturée au prix exact (SKU MS-SM-…), la
   * ligne est en quantité 1 au prix total : sa surface est dans « Surface
   * facturée » (« 8,21 m² »). Sinon (secours au m² entamé, Personnalisation),
   * la quantité est le nombre de m².
   * @param {any} item
   */
  const lineM2 = (item) => {
    if (String(item.sku || '').startsWith('MS-SM-')) {
      const m2 = parseFloat(String(item.properties?.['Surface facturée'] || '').replace(/\s/g, '').replace(',', '.'));
      if (m2 > 0) return m2 * item.quantity;
    }
    return item.quantity;
  };

  /** @param {any} cart */
  const decorM2 = (cart) => cart.items.filter(isDecor).reduce((sum, item) => sum + lineM2(item), 0);

  /** Adresse .js d'une route du panier, que la route du thème porte déjà « .js » ou non. */
  const route = (key, fallback) => `${(window.Theme?.routes?.[key] ?? fallback).replace(/\.js$/, '')}.js`;

  /**
   * @param {string} url
   * @param {object} body
   */
  const post = async (url, body) => {
    const response = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) throw new Error(data.description || data.message || 'Panier indisponible');
    return data;
  };

  /**
   * Met la ligne pose d'accord avec les décors du panier.
   * @param {{want?: boolean, sections?: string|null, sectionsUrl?: string}} [options]
   *   want : true pour demander la pose, undefined pour garder le choix actuel.
   * @returns {Promise<{changed:boolean, quote:ReturnType<typeof quote>, sections?:object, itemCount?:number}>}
   */
  const sync = async ({ want, sections = null, sectionsUrl } = {}) => {
    const cart = await (await fetch(route('cart_url', '/cart'), { headers: { Accept: 'application/json' } })).json();
    const lines = cart.items.filter(isPose);
    const wanted = want ?? lines.length > 0;
    const q = quote(decorM2(cart));
    const allowed = wanted && q.state === 'ok' && countryAllowed();
    const label = `${q.m2} m²`;

    const same = allowed
      && lines.length === 1
      && lines[0].variant_id === q.variantId
      && lines[0].quantity === q.quantity
      && lines[0].properties?.[config.prop] === label;
    if (same || (!allowed && lines.length === 0)) return { changed: false, quote: q, itemCount: cart.item_count };

    const extra = sections ? { sections, sections_url: sectionsUrl ?? window.location.pathname } : {};
    let state = null;
    if (lines.length) {
      const updates = Object.fromEntries(lines.map((line) => [line.key, 0]));
      state = await post(route('cart_update_url', '/cart/update'), { updates, ...(allowed ? {} : extra) });
    }
    if (allowed) {
      const properties = { [config.prop]: label };
      state = await post(route('cart_add_url', '/cart/add'), { items: [{ id: q.variantId, quantity: q.quantity, properties }], ...extra });
      // /cart/add.js renvoie les lignes ajoutées, pas le panier : on relit le compte.
      const fresh = await (await fetch(route('cart_url', '/cart'))).json();
      return { changed: true, quote: q, sections: state?.sections, itemCount: fresh.item_count };
    }
    return { changed: true, quote: q, sections: state?.sections, itemCount: state?.item_count };
  };

  window.MsPose = { config, money, quote, sync, countryAllowed, decorM2 };

  // La page panier se recharge sans événement : on remet la pose d'accord à
  // l'arrivée. Le compteur de session évite toute boucle de rechargement.
  const onCartPage = () => Boolean(document.querySelector('[data-ms-cart-page]'));
  const syncCartPage = async () => {
    if (!config || !onCartPage()) return;
    let tries = 0;
    try { tries = Number(sessionStorage.getItem('ms-pose-sync') || 0); } catch { /* stockage indisponible */ }
    if (tries >= 2) { try { sessionStorage.removeItem('ms-pose-sync'); } catch { /* idem */ } return; }
    try {
      const result = await sync();
      if (!result.changed) { try { sessionStorage.removeItem('ms-pose-sync'); } catch { /* idem */ } return; }
      try { sessionStorage.setItem('ms-pose-sync', String(tries + 1)); } catch { /* idem */ }
      window.location.reload();
    } catch { /* le panier reste tel quel */ }
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', syncCartPage);
  else syncCartPage();

  // Un décor retiré ou ajouté ailleurs (tiroir, page panier, autre fiche) :
  // la pose suit. Nos propres mises à jour portent la source « ms-pose ».
  let running = false;
  document.addEventListener('cart:updated', async (event) => {
    if (!config || running) return;
    if (event.detail?.data?.source === 'ms-pose') return;
    running = true;
    try {
      const drawer = document.querySelector('cart-drawer');
      const sectionId = drawer?.dataset?.sectionId ?? null;
      const result = await sync({ sections: sectionId });
      if (!result.changed) return;
      if (onCartPage()) {
        window.location.reload();
        return;
      }
      document.dispatchEvent(new CustomEvent('cart:updated', {
        bubbles: true,
        detail: { resource: {}, data: { source: 'ms-pose', itemCount: result.itemCount, sections: result.sections } },
      }));
    } catch {
      /* le panier reste tel quel ; la prochaine mise à jour retentera */
    } finally {
      running = false;
    }
  });
})();
